/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Avatar,
  Button,
  Dropdown,
  Image,
  TableColumnsType,
  Tag,
  Tooltip,
} from "antd";
import Header from "../../atoms/Header";
import TableUi from "../../atoms/Table";
import DashboardTemplate from "../../templates/Dashboard";
import Filters from "./Components/Filters";
import { useEffect, useRef, useState } from "react";
import { ClientsType } from "./ducks/types";
import { PopUp } from "../../atoms/PopUp";
import EmptyCard from "../../atoms/EmptyCard";
import {
  CrossIcon,
  DeleteIcon,
  NeedHelp,
  PlusIcon,
  TickIcon,
} from "../../atoms/CustomIcons";
import ConfirmDelete from "../../atoms/ConfirmDelete";
import { useAppDispatch, useAppSelector } from "../../config/hooks";
import { getClientListing, removeClientListData } from "./ducks/actions";
import { changeClientStatus, deleteClient } from "./ducks/services";
import { SuccessMessage } from "../../atoms/Toasts/SuccessMessage";
import { ErrorMessage } from "../../atoms/Toasts/ErrorMessage";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { getCompanyFieldsById } from "../CompanySettings/TagsCategories/ducks/actions";
import { CaretDownOutlined, MoreOutlined } from "@ant-design/icons";
import { PAGE_LIMIT } from "../../config/utils";

export default function Clients() {
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const navigate = useNavigate();
  const companyId = pathSegments[pathSegments.length - 1];
  const [pagination, setPagination] = useState(1);
  const [limit, setLimit] = useState(PAGE_LIMIT);
  const [tableRecord, setTableRecord] = useState<ClientsType | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [archivePopUp, setArchivePopUp] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | string[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [archived, isArchived] = useState(false);
  const dispatch = useAppDispatch();
  const clientListData = useAppSelector(
    (state) => state?.clients?.clientListData
  );

  const tagItems = useAppSelector((state) => state?.tags?.tagData);

  useEffect(() => {
    if (companyId) {
      const payload = {
        company_id: companyId,
        module: "CLIENTS",
      };

      dispatch(getCompanyFieldsById(payload));
    }
  }, [dispatch, companyId]);

  const [filters, setFilters] = useState<{
    type: undefined | string;
    status: undefined | string[];
    state: undefined;
    city: undefined;
    last_user_activity: string[] | undefined;
    onboarding_complete_date: string[] | undefined;
    search_text: undefined | string;
  }>({
    type: undefined,
    status: undefined,
    state: undefined,
    city: undefined,
    last_user_activity: undefined,
    onboarding_complete_date: undefined,
    search_text: undefined,
  });

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            localStorage.setItem(
              "client_id_for_add_property",
              tableRecord?.id || ""
            );
            navigate(`/add-property/${companyId}`);
          }}
          icon={<PlusIcon />}
        >
          Add property
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/edit-client/${tableRecord?.id}/${companyId}`);
          }}
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
        >
          Edit details
        </Button>
      ),
    },
    {
      key: "3",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();

            tableRecord?.status === "active"
              ? setArchivePopUp(true)
              : archiveFunction();
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
          icon={
            <Image
              src={`/images/table/${
                tableRecord?.status === "active" ? "archive" : "activate"
              }-icon.svg`}
              preview={false}
            />
          }
        >
          {tableRecord?.status === "active" ? "Archive" : "Activate"}
        </Button>
      ),
    },
    {
      key: "4",
      label: (
        <Button
          type="text"
          className="delete-hover"
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
          icon={<DeleteIcon />}
        >
          Delete
        </Button>
      ),
    },
  ];

  const statusItems = [
    {
      key: "active",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            archiveFunction();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#00A07D", borderRadius: "2px" }}
          />
          Active
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
    {
      key: "archived",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            setArchivePopUp(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#6C7478", borderRadius: "2px" }}
          />
          Archived
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
  ];

  const columns: TableColumnsType<ClientsType> = [
    {
      title: "Client name",
      dataIndex: "name",
      width: 281,
      render: (text, record) => (
        <Tooltip title={text ? `${text} ${record?.last_name ?? ""}` : "-"}>
          <span className="table-col-ellipsis" style={{ maxWidth: "275px" }}>
            {text ? `${text} ${record?.last_name ?? ""}` : "-"}
          </span>
        </Tooltip>
      ),
      fixed: "left",
      sorter: (a, b) => a?.name?.localeCompare(b?.name),
      showSorterTooltip: false,
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 150,
      render: (text) =>
        text ? (
          <Tag className="white_tag" style={{ textTransform: "capitalize" }}>
            {text}
          </Tag>
        ) : (
          <Tag> - </Tag>
        ),
      sorter: (a, b) => a?.type?.localeCompare(b?.type),
      showSorterTooltip: false,
    },
    {
      title: "Properties",
      dataIndex: "properties",
      width: 120,
      ellipsis: true,
      render: (text: { id: string; name: string }[]) =>
        Array.isArray(text) && text.length > 0 ? (
          <div style={{ display: "flex", gap: "4px" }}>
            {text.slice(0, 1).map((item: { id: string; name: string }) => {
              return (
                <Tag key={item?.id} style={{ textTransform: "capitalize" }}>
                  {item?.name}
                </Tag>
              );
            })}

            {text.length > 1 && (
              <Tooltip
                title={text
                  .slice(1)
                  .map((e: { id: string; name: string }) => e?.name)
                  .join(", ")}
              >
                <Tag
                  style={{
                    background: "#EFF2F3",
                    color: "#151718",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  +{text.length - 1}
                </Tag>
              </Tooltip>
            )}
          </div>
        ) : (
          "-"
        ),
      align: "left",
      // className: "right-aligned-sorter",
      sorter: (a, b) => {
        const aLen = Array.isArray(a?.properties) ? a.properties.length : 0;
        const bLen = Array.isArray(b?.properties) ? b.properties.length : 0;
        return aLen - bLen;
      },
      showSorterTooltip: false,
    },
    {
      title: "City",
      dataIndex: "city",
      width: 160,
      render: (text: string) => (
        <Tooltip title={text ? text : "-"}>
          <span className="table-col-ellipsis" style={{ maxWidth: "155px" }}>
            {text ? text : "-"}
          </span>
        </Tooltip>
      ),
      sorter: (a, b) => a?.city?.localeCompare(b?.city),
      showSorterTooltip: false,
    },
    {
      title: "State",
      dataIndex: "state",
      width: 160,
      render: (text: string) => (
        <Tooltip title={text ? text : "-"}>
          <span className="table-col-ellipsis" style={{ maxWidth: "155px" }}>
            {text ? text : "-"}
          </span>
        </Tooltip>
      ),
      sorter: (a, b) => a?.state?.localeCompare(b?.state),
      showSorterTooltip: false,
    },
    {
      title: (
        <Tooltip title="Shows if there’s an ongoing contract or a potential deal">
          Contract status
        </Tooltip>
      ),
      dataIndex: "engagement_status",
      width: 145,
      render: (text) =>
        text ? <Tag className="yellow_tag"> {text} </Tag> : "-",
      sorter: (a, b) =>
        a?.engagement_status?.localeCompare(b?.engagement_status),
      showSorterTooltip: false,
    },
    // {
    //   title: (
    //     <Tooltip title="Number of open potential deals">
    //       Potential deals
    //     </Tooltip>
    //   ),
    //   dataIndex: "deals_count",
    //   width: 163,
    //   render: (text) => (text ? text : "0"),
    //   align: "right",
    //   className: "right-aligned-sorter",
    //   sorter: (a, b) => a?.potential_deals?.localeCompare(b?.potential_deals),
    //   showSorterTooltip: false,
    // },
    // {
    //   title: (
    //     <Tooltip title="Number of ongoing contracts">Open contracts</Tooltip>
    //   ),
    //   dataIndex: "open_contracts",
    //   width: 163,
    //   render: (text) => (text ? text : "0"),
    //   align: "right",
    //   className: "right-aligned-sorter",
    //   sorter: (a, b) => a?.open_contracts?.localeCompare(b?.open_contracts),
    //   showSorterTooltip: false,
    // },
    // {
    //   title: (
    //     <Tooltip title="Main point of contact on the client’s side">
    //       Main contact
    //     </Tooltip>
    //   ),
    //   dataIndex: "clientContacts",
    //   width: 180,
    //   render: (text) =>
    //     Array.isArray(text) &&
    //     text.length > 0 &&
    //     text.some((item: { is_starred?: boolean }) => item?.is_starred) ? (
    //       <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
    //         {text
    //           .filter((item: { is_starred?: boolean }) => item?.is_starred)
    //           .slice(0, 1)
    //           .map((item: any, idx: number) => {
    //             return (
    //               <Tag
    //                 key={item?.id || idx}
    //                 style={{ textTransform: "capitalize" }}
    //               >
    //                 {`${item?.first_name || "-"} ${item?.last_name || "-"}` ||
    //                   "-"}
    //               </Tag>
    //             );
    //           })}
    //       </div>
    //     ) : (
    //       "-"
    //     ),
    //   // sorter: (a, b) => a?.main_contracts?.localeCompare(b?.main_contracts),
    //   // showSorterTooltip: false,
    // },
    // {
    //   title: (
    //     <Tooltip title="Contact person for billing and payment-related matters">
    //       Billing contact
    //     </Tooltip>
    //   ),
    //   dataIndex: "clientContacts",
    //   width: 180,
    //   render: (text) =>
    //     Array.isArray(text) &&
    //     text.length > 0 &&
    //     text.some((item: { is_billing?: boolean }) => item?.is_billing) ? (
    //       <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
    //         {text
    //           .filter((item: { is_billing?: boolean }) => item?.is_billing)
    //           .slice(0, 1)
    //           .map((item: any, idx: number) => {
    //             return (
    //               <Tag
    //                 key={item?.id || idx}
    //                 style={{ textTransform: "capitalize" }}
    //               >
    //                 {`${item?.first_name || "-"} ${item?.last_name || "-"}` ||
    //                   "-"}
    //               </Tag>
    //             );
    //           })}

    //         {text.filter((item: { is_billing?: boolean }) => item?.is_billing)
    //           .length > 1 && (
    //           <Tooltip
    //             title={text
    //               .filter((item: { is_billing?: boolean }) => item?.is_billing)
    //               .slice(1)
    //               .map((item: any) => {
    //                 return (
    //                   `${item?.first_name || "-"} ${item?.last_name || "-"}` ||
    //                   "-"
    //                 );
    //               })
    //               .join(", ")}
    //           >
    //             <Tag
    //               style={{
    //                 background: "#EFF2F3",
    //                 color: "#151718",
    //                 border: "none",
    //                 cursor: "pointer",
    //               }}
    //             >
    //               +
    //               {text.filter(
    //                 (item: { is_billing?: boolean }) => item?.is_billing
    //               ).length - 1}
    //             </Tag>
    //           </Tooltip>
    //         )}
    //       </div>
    //     ) : (
    //       "-"
    //     ),
    //   // sorter: (a, b) =>
    //   //   a?.billing_contracts?.localeCompare(b?.billing_contracts),
    //   // showSorterTooltip: false,
    // },
    {
      title: "Category",
      dataIndex: "category_ids",
      width: 260,
      render: (text: string[]) =>
        text?.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {text?.slice(0, 1).map((item: string) => {
              const tagDetails: { color: string; name: string } =
                tagItems?.data?.find(
                  (tag: { id: string }) => tag?.id === item
                ) || { color: "", name: "" };

              return (
                <Tag
                  key={item}
                  style={{ textTransform: "capitalize" }}
                  className={tagDetails?.color}
                >
                  {tagDetails?.name}
                </Tag>
              );
            })}

            {text?.length > 1 && (
              <Tooltip
                title={text
                  ?.slice(1)
                  ?.map((itemId: string) => {
                    const tagDetails: { name: string } = tagItems?.data?.find(
                      (tag: { id: string }) => tag?.id === itemId
                    ) || { name: "" };
                    return tagDetails?.name;
                  })
                  ?.join(", ")}
              >
                <Tag
                  style={{
                    background: "#EFF2F3",
                    color: "#151718",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  +{text?.length - 1}
                </Tag>
              </Tooltip>
            )}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: (
        <Tooltip title="Total amount of all active contracts">
          Active contracts sum, $
        </Tooltip>
      ),
      dataIndex: "active_contracts_sum",
      width: 204,
      render: (text) => (text ? text : "-"),
      align: "right",
      className: "right-aligned-sorter",
      sorter: (a, b) =>
        a?.active_contracts_sum?.localeCompare(b?.active_contracts_sum),
      showSorterTooltip: false,
    },
    {
      title: (
        <Tooltip title="Sum of all unpaid invoices">
          Accounts receivable, $
        </Tooltip>
      ),
      dataIndex: "accounts_receivable",
      width: 204,
      render: (text) => (text ? text : "-"),
      align: "right",
      className: "right-aligned-sorter",
      sorter: (a, b) =>
        a?.accounts_receivable?.localeCompare(b?.accounts_receivable),
      showSorterTooltip: false,
    },
    {
      title: (
        <Tooltip title="Earned revenue, year-to-date">
          Earned revenue (YTD), $
        </Tooltip>
      ),
      dataIndex: "earned_revenue_ytd",
      width: 205,
      render: (text) => (text ? text : "-"),
      align: "right",
      className: "right-aligned-sorter",
      sorter: (a, b) =>
        a?.earned_revenue_ytd?.localeCompare(b?.earned_revenue_ytd),
      showSorterTooltip: false,
    },
    {
      title: <Tooltip title="Gross margin, year-to-date">GM (YTD), %</Tooltip>,
      dataIndex: "gross_margin",
      width: 130,
      render: (text) => (text ? text : "-"),
      align: "right",
      className: "right-aligned-sorter",
      sorter: (a, b) => a?.gross_margin?.localeCompare(b?.gross_margin),
      showSorterTooltip: false,
    },
    {
      title: (
        <Tooltip title="Gross profit, year-to-date">
          Gross profit (R12M), $
        </Tooltip>
      ),
      dataIndex: "gross_profit",
      width: 200,
      render: (text) => (text ? text : "-"),
      align: "right",
      className: "right-aligned-sorter",
      sorter: (a, b) => a?.gross_profit?.localeCompare(b?.gross_profit),
      showSorterTooltip: false,
    },
    {
      title: <Tooltip title="Win ratio, year-to-date">Win ratio (YTD)</Tooltip>,
      dataIndex: "wind_ratio",
      width: 150,
      render: (text) => (text ? text : "-"),
      align: "right",
      className: "right-aligned-sorter",
      sorter: (a, b) => a?.wind_ratio?.localeCompare(b?.wind_ratio),
      showSorterTooltip: false,
    },
    {
      title: (
        <Tooltip title="Earned revenue, rolling 12 months">
          Earned revenue (R12M), $
        </Tooltip>
      ),
      dataIndex: "earned_revenue_r12m",
      width: 225,
      render: (text) => (text ? text : "-"),
      align: "right",
      className: "right-aligned-sorter",
      sorter: (a, b) =>
        a?.earned_revenue_r12m?.localeCompare(b?.earned_revenue_r12m),
      showSorterTooltip: false,
    },
    {
      title: (
        <Tooltip title="Gross margin, rolling 12 months">GM (R12M), %</Tooltip>
      ),
      dataIndex: "gross_margin_r12m",
      width: 150,
      render: (text) => (text ? text : "-"),
      align: "right",
      className: "right-aligned-sorter",
      sorter: (a, b) =>
        a?.gross_margin_r12m?.localeCompare(b?.gross_margin_r12m),
      showSorterTooltip: false,
    },
    {
      title: (
        <Tooltip title="Gross profit, rolling 12 months">
          Gross profit (R12M), $
        </Tooltip>
      ),
      dataIndex: "gross_profit_r12m",
      width: 200,
      render: (text) => (text ? text : "-"),
      align: "right",
      className: "right-aligned-sorter",
      sorter: (a, b) =>
        a?.gross_profit_r12m?.localeCompare(b?.gross_profit_r12m),
      showSorterTooltip: false,
    },
    {
      title: (
        <Tooltip title="Win ratio, rolling 12 months">Win ratio (R12M)</Tooltip>
      ),
      dataIndex: "wind_ratio_r12m",
      width: 170,
      render: (text) => (text ? text : "-"),
      align: "right",
      className: "right-aligned-sorter",
      sorter: (a, b) => a?.wind_ratio_r12m?.localeCompare(b?.wind_ratio_r12m),
      showSorterTooltip: false,
    },
    {
      title: "Client status",
      dataIndex: "status",
      width: 140,
      render: (text, record) => (
        <Dropdown
          menu={{
            items: statusItems,
            selectable: true,
            selectedKeys: [text],
          }}
          trigger={["click"]}
          overlayClassName="status_dropdown_overlay"
        >
          <Button
            onClick={(e) => {
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            className={`status_dropdown ${text}_status`}
            ref={dropdownRef}
            style={{ textTransform: "capitalize" }}
          >
            {text}{" "}
            <div className="icon_arrow">
              <CaretDownOutlined />
            </div>
          </Button>
        </Dropdown>
      ),
    },
    {
      title: <Image src="/images/table/menu-burger.svg" preview={false} />,
      dataIndex: "action",
      width: 52,
      fixed: "right",
      render: (_text, record) => (
        <Dropdown
          menu={{
            items:
              record?.status === "active"
                ? items
                : items.filter(
                    (item) => item?.key !== "1" && item?.key !== "2"
                  ),
          }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(e) => {
              console.log("text", record);
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              padding: "6px",
              height: "auto",
              background: "transparent",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
            ref={dropdownRef}
          />
        </Dropdown>
      ),
    },
  ];

  const fetchUserListing = (status: string) => {
    const payload = {
      company_id: companyId,
      offset: 0,
      limit: PAGE_LIMIT,
      status: [status ?? "active"],
    };
    dispatch(getClientListing(payload));
  };

  const deleteFunction = () => {
    setLoading(true);
    deleteClient(
      selectedRowKeys?.length > 0 ? selectedRowKeys : [tableRecord?.id || ""]
    )
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        setSelectedRowKeys([]);
        fetchUserListing(tableRecord?.status ?? "active");
        SuccessMessage("Client deleted");
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const deleteClientPopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete{" "}
            {selectedRowKeys?.length > 0 ? (
              <>the selected client{selectedRowKeys.length > 1 ? "s" : ""}?</>
            ) : (
              <>
                <b>{tableRecord?.name}</b>?
              </>
            )}
            <br />
            <br />
            This action cannot be undone.
          </>
        }
        btnText={
          selectedRowKeys?.length > 1
            ? `Delete ${selectedRowKeys.length} clients`
            : "Delete client"
        }
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const archiveFunction = () => {
    setLoading(true);
    const payload = {
      status: tableRecord?.status === "active" ? "archived" : "active",
    };
    changeClientStatus(tableRecord?.id as string, payload)
      .then(() => {
        setLoading(false);
        setArchivePopUp(false);
        fetchUserListing(tableRecord?.status ?? "active");
        SuccessMessage(
          `Client ${
            tableRecord?.status === "active" ? "archived" : "activated"
          }`
        );
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const confirmArchivedPopUp = {
    visibility: archivePopUp,
    content: (
      <ConfirmDelete
        title=""
        isArchivePopup={true}
        onClose={() => setArchivePopUp(false)}
        heading={
          tableRecord?.status === "active"
            ? "Archive client"
            : "Activate client"
        }
        paragraph={
          <>
            Are you sure you want to{" "}
            {tableRecord?.status === "active" ? "archive" : "activate"}{" "}
            <b>{tableRecord?.name}</b>? <br /> <br />
            {tableRecord?.status === "active" && (
              <>This client won’t be available for use.</>
            )}
          </>
        }
        btnText={`${
          tableRecord?.status === "active" ? "Archive" : "Activate"
        } client`}
        btnApi={archiveFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setArchivePopUp(false),
  };

  useEffect(() => {
    dispatch(removeClientListData());
  }, [dispatch]);

  useEffect(() => {
    if (companyId) {
      const payload = {
        status: filters?.status ?? ["active"],
        offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
        limit: limit,
        company_id: companyId,
        type: filters?.type ?? undefined,
        search_text: filters?.search_text ?? undefined,
      };
      dispatch(getClientListing(payload));
      console.log("payload", payload);
    }
  }, [dispatch, filters, pagination, limit, companyId]);

  const onClickRow = (record: ClientsType) => {
    return {
      onClick: () => {
        navigate(`/edit-client/${record?.id}/${companyId}`);
      },
    };
  };

  return (
    <DashboardTemplate loading={loading || clientListData?.loader}>
      <Header title="Clients" btnText1="Need help?" btnIcon1={<NeedHelp />} />

      <TableUi<ClientsType>
        columns={columns}
        data={{
          count: clientListData?.count,
          data: clientListData?.data || [],
        }}
        filters={
          <Filters
            setFilters={setFilters}
            filters={filters}
            archived={archived}
            isArchived={isArchived}
          />
        }
        pageSize={limit}
        setPageSize={setLimit}
        setCurrentPage={setPagination}
        currentPage={pagination}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        createButtonText="Add new client"
        createButtonLink={`/add-client/${companyId}`}
        onClickRow={onClickRow}
      />
      {!clientListData?.loader && clientListData?.data?.length === 0 && (
        <div className="empty_wrap">
          <EmptyCard
            imageHeight={220}
            title={
              archived
                ? "There are no archived clients"
                : filters?.search_text
                ? "No results match the filter or search criteria"
                : `There are no ${filters?.status ?? "active"} clients`
            }
            description={
              archived
                ? "We believe in our absolute success and hope to never have any archived clients here."
                : filters?.search_text
                ? "Try adjusting the filters and search."
                : ""
            }
            image={
              archived
                ? "/images/empty-icons/no-archived.svg"
                : "/images/empty-icons/no-active.svg"
            }
          />
        </div>
      )}

      {selectedRowKeys?.length !== 0 ? (
        <div className="selection_bar">
          <div className="selected_count">
            <Button
              icon={<CrossIcon style={{ color: "white" }} />}
              className="only_icon"
              type="text"
              onClick={() => setSelectedRowKeys([])}
            />
            {selectedRowKeys.length}{" "}
            {`${selectedRowKeys.length === 1 ? "client" : "clients"}`} selected
          </div>
          <div className="selection_buttons">
            <Button
              type="text"
              icon={<DeleteIcon />}
              onClick={() => {
                setIsDeleteModalOpen(true);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}

      <PopUp {...deleteClientPopUp} />
      <PopUp {...confirmArchivedPopUp} />
    </DashboardTemplate>
  );
}
